import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Footer } from "../components/Footer";
import { LanguageToggle } from "../components/LanguageToggle";
import { useReactiveLang } from "../lib/i18n";

const CONTACT = "legal@islandsdaily.com";
const LAST_UPDATED = "February 2026";

const LANGS = [
  { code: "en", label: "English" },
  { code: "nl", label: "Nederlands" },
  { code: "es", label: "Español" },
];

const COPY = {
  en: {
    title: "Privacy Policy",
    last_updated: `Last updated: ${LAST_UPDATED}`,
    intro:
      'IslandsDaily ("we," "us," "our") operates islandsdaily.com and the IslandsDaily WhatsApp digest service. This Privacy Policy explains what information we collect, how we use it, and your rights regarding that information.',
    sections: [
      {
        h: "Information We Collect",
        p: "When you subscribe to the IslandsDaily WhatsApp digest, we collect:",
        list: [
          "Your WhatsApp phone number",
          "Your name, if you choose to provide it (optional)",
          "Your selected island(s) of interest",
          "Your preferred digest language (English, Dutch, or Spanish)",
        ],
        p_after:
          "We do not collect payment information, government ID numbers, or any other sensitive personal data through the subscription process.",
      },
      {
        h: "How We Use Your Information",
        p: "We use your information solely to:",
        list: [
          "Deliver your daily WhatsApp news digest",
          "Personalize the digest to your selected islands and language",
          "Respond to HELP requests or manage opt-outs",
        ],
        p_after:
          "We do not sell, rent, or share your phone number or personal information with third parties for marketing purposes.",
      },
      {
        h: "Third-Party Service Providers",
        p: "We use Twilio, a messaging infrastructure provider, to deliver WhatsApp messages. Twilio processes your phone number solely to transmit messages on our behalf and is bound by its own data protection obligations. We do not share your data with any other third party.",
      },
      {
        h: "Message Frequency and Opt-Out",
        p: "You will receive up to one (1) WhatsApp message per day. Message and data rates may apply depending on your mobile carrier or plan. You may unsubscribe at any time by replying STOP to any digest message. Reply HELP for assistance.",
        p_after:
          "No mobile information will be shared with third parties or affiliates for marketing or promotional purposes. All other categories exclude text messaging originator opt-in data and consent; this information will not be shared with any third parties.",
      },
      {
        h: "Data Retention",
        p: "We retain your subscription information for as long as your subscription remains active. If you unsubscribe, your phone number is deactivated in our system and excluded from future sends. You may request full deletion of your data at any time by contacting us (see below).",
      },
      {
        h: "Your Rights",
        p: "You may request access to, correction of, or deletion of your personal information at any time by contacting us at the email below.",
      },
      {
        h: "Contact",
        p: "For any privacy questions or data requests, contact:",
        contact: true,
      },
      {
        h: "Changes to This Policy",
        p: "We may update this Privacy Policy from time to time. Continued use of the service after changes constitutes acceptance of the updated policy.",
      },
    ],
  },
  nl: {
    title: "Privacybeleid",
    last_updated: `Laatst bijgewerkt: ${LAST_UPDATED}`,
    intro:
      'IslandsDaily ("wij", "ons", "onze") exploiteert islandsdaily.com en de IslandsDaily WhatsApp-digest-dienst. Dit Privacybeleid legt uit welke informatie we verzamelen, hoe we deze gebruiken en welke rechten u heeft met betrekking tot die informatie.',
    sections: [
      {
        h: "Informatie Die Wij Verzamelen",
        p: "Wanneer u zich abonneert op de IslandsDaily WhatsApp-digest, verzamelen wij:",
        list: [
          "Uw WhatsApp-telefoonnummer",
          "Uw naam, indien u ervoor kiest deze op te geven (optioneel)",
          "Uw geselecteerde eiland(en) van interesse",
          "Uw voorkeurstaal voor de digest (Engels, Nederlands of Spaans)",
        ],
        p_after:
          "Wij verzamelen geen betalingsgegevens, overheidsidentificatienummers of andere gevoelige persoonsgegevens via het abonnementsproces.",
      },
      {
        h: "Hoe Wij Uw Informatie Gebruiken",
        p: "Wij gebruiken uw informatie uitsluitend om:",
        list: [
          "Uw dagelijkse WhatsApp-nieuwsdigest te bezorgen",
          "De digest af te stemmen op uw geselecteerde eilanden en taal",
          "Te reageren op HELP-verzoeken of afmeldingen te beheren",
        ],
        p_after:
          "Wij verkopen, verhuren of delen uw telefoonnummer of persoonlijke informatie niet met derden voor marketingdoeleinden.",
      },
      {
        h: "Externe Dienstverleners",
        p: "Wij gebruiken Twilio, een aanbieder van berichteninfrastructuur, om WhatsApp-berichten te bezorgen. Twilio verwerkt uw telefoonnummer uitsluitend om berichten namens ons te verzenden en is gebonden aan zijn eigen gegevensbeschermingsverplichtingen. Wij delen uw gegevens met geen enkele andere derde partij.",
      },
      {
        h: "Berichtfrequentie en Afmelden",
        p: "U ontvangt maximaal één (1) WhatsApp-bericht per dag. Er kunnen bericht- en datakosten van toepassing zijn, afhankelijk van uw mobiele provider of abonnement. U kunt zich op elk moment afmelden door STOP te antwoorden op een digest-bericht. Antwoord HELP voor hulp.",
        p_after:
          "Geen mobiele informatie wordt gedeeld met derden of gelieerde ondernemingen voor marketing- of promotionele doeleinden. Alle andere categorieën sluiten opt-in-gegevens en toestemming van de afzender van sms-berichten uit; deze informatie zal niet worden gedeeld met derden.",
      },
      {
        h: "Gegevensbewaring",
        p: "Wij bewaren uw abonnementsgegevens zolang uw abonnement actief blijft. Als u zich afmeldt, wordt uw telefoonnummer gedeactiveerd in ons systeem en uitgesloten van toekomstige verzendingen. U kunt op elk moment volledige verwijdering van uw gegevens verzoeken door contact met ons op te nemen (zie hieronder).",
      },
      {
        h: "Uw Rechten",
        p: "U kunt op elk moment toegang tot, correctie van of verwijdering van uw persoonlijke informatie aanvragen door contact met ons op te nemen via het onderstaande e-mailadres.",
      },
      {
        h: "Contact",
        p: "Voor privacyvragen of gegevensverzoeken kunt u contact opnemen met:",
        contact: true,
      },
      {
        h: "Wijzigingen in Dit Beleid",
        p: "Wij kunnen dit Privacybeleid van tijd tot tijd bijwerken. Voortgezet gebruik van de dienst na wijzigingen betekent acceptatie van het bijgewerkte beleid.",
      },
    ],
  },
  es: {
    title: "Política de Privacidad",
    last_updated: `Última actualización: ${LAST_UPDATED}`,
    intro:
      'IslandsDaily ("nosotros", "nos", "nuestro") opera islandsdaily.com y el servicio de resumen diario por WhatsApp de IslandsDaily. Esta Política de Privacidad explica qué información recopilamos, cómo la utilizamos y sus derechos con respecto a esa información.',
    sections: [
      {
        h: "Información Que Recopilamos",
        p: "Cuando se suscribe al resumen diario por WhatsApp de IslandsDaily, recopilamos:",
        list: [
          "Su número de teléfono de WhatsApp",
          "Su nombre, si decide proporcionarlo (opcional)",
          "Su(s) isla(s) de interés seleccionada(s)",
          "Su idioma preferido para el resumen (inglés, neerlandés o español)",
        ],
        p_after:
          "No recopilamos información de pago, números de identificación gubernamental ni ningún otro dato personal sensible a través del proceso de suscripción.",
      },
      {
        h: "Cómo Usamos Su Información",
        p: "Utilizamos su información únicamente para:",
        list: [
          "Entregar su resumen diario de noticias por WhatsApp",
          "Personalizar el resumen según sus islas e idioma seleccionados",
          "Responder a solicitudes de HELP o gestionar cancelaciones",
        ],
        p_after:
          "No vendemos, alquilamos ni compartimos su número de teléfono o información personal con terceros con fines de marketing.",
      },
      {
        h: "Proveedores de Servicios Externos",
        p: "Utilizamos Twilio, un proveedor de infraestructura de mensajería, para entregar mensajes de WhatsApp. Twilio procesa su número de teléfono únicamente para transmitir mensajes en nuestro nombre y está sujeto a sus propias obligaciones de protección de datos. No compartimos sus datos con ningún otro tercero.",
      },
      {
        h: "Frecuencia de Mensajes y Cancelación",
        p: "Recibirá hasta un (1) mensaje de WhatsApp por día. Pueden aplicarse cargos por mensajes y datos según su operador o plan móvil. Puede darse de baja en cualquier momento respondiendo STOP a cualquier mensaje del resumen. Responda HELP para obtener ayuda.",
        p_after:
          "Ninguna información móvil será compartida con terceros o afiliados para fines de marketing o promocionales. Todas las demás categorías excluyen los datos de consentimiento y aceptación (opt-in) del originador de mensajes de texto; esta información no será compartida con ningún tercero.",
      },
      {
        h: "Conservación de Datos",
        p: "Conservamos su información de suscripción mientras su suscripción permanezca activa. Si cancela la suscripción, su número de teléfono se desactiva en nuestro sistema y se excluye de envíos futuros. Puede solicitar la eliminación total de sus datos en cualquier momento poniéndose en contacto con nosotros (véase a continuación).",
      },
      {
        h: "Sus Derechos",
        p: "Puede solicitar acceso, corrección o eliminación de su información personal en cualquier momento poniéndose en contacto con nosotros en el correo electrónico que aparece a continuación.",
      },
      {
        h: "Contacto",
        p: "Para cualquier pregunta sobre privacidad o solicitud de datos, contacte:",
        contact: true,
      },
      {
        h: "Cambios en Esta Política",
        p: "Podemos actualizar esta Política de Privacidad de vez en cuando. El uso continuado del servicio después de los cambios constituye la aceptación de la política actualizada.",
      },
    ],
  },
};

export default function PrivacyPage() {
  // __TOGGLE_HOOK__: self-contained reactive lang state for the mini-header toggle.
  const [__toggleLang, __setToggleLang] = useReactiveLang();

  const [lang, setLang] = useState("en");
  const copy = COPY[lang];

  useEffect(() => {
    document.title = "Privacy Policy — IslandsDaily";
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="privacy-page">
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-[#EEEEEE]">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-[13px] text-[#6B7C82] hover:text-[#1A2E35]"
            data-testid="privacy-back-btn"
          >
            <ArrowLeft size={15} /> Back
          </Link>
          <Link
            to="/"
            className="ml-auto font-serif-display text-[18px] leading-none"
          >
            <span className="text-[#1A1A1A]">Islands</span>
            <span className="text-[#0AADA0]">Daily</span>
          </Link>
          {/* __LANG_TOGGLE__ */}
          <LanguageToggle lang={__toggleLang} onChange={__setToggleLang} />
        </div>
      </div>

      <article
        className="mx-auto px-4 sm:px-6 py-10 sm:py-14"
        style={{ maxWidth: "760px" }}
      >
        <h1
          className="font-serif-display text-[32px] sm:text-[36px] leading-tight text-[#1A2E35] text-center"
          data-testid="privacy-heading"
        >
          {copy.title}
        </h1>
        <p className="text-center text-[12px] text-[#6B7C82] mt-2 mb-8">
          {copy.last_updated}
        </p>

        <div
          className="flex items-center justify-center gap-2 mb-10"
          role="tablist"
          data-testid="privacy-lang-tabs"
        >
          {LANGS.map((l) => (
            <button
              key={l.code}
              role="tab"
              aria-selected={lang === l.code}
              onClick={() => setLang(l.code)}
              className={`px-4 py-1.5 rounded-full text-[12px] transition-colors ${
                lang === l.code
                  ? "bg-[#0AADA0] text-white"
                  : "bg-[#F5F5F5] text-[#1A2E35] hover:bg-[#EEEEEE]"
              }`}
              data-testid={`privacy-lang-${l.code}`}
            >
              {l.label}
            </button>
          ))}
        </div>

        <div
          className="space-y-7 text-[16px] text-[#1A2E35]"
          style={{
            lineHeight: 1.8,
            fontFamily: "'DM Sans', system-ui, sans-serif",
          }}
          data-testid={`privacy-content-${lang}`}
        >
          <p>{copy.intro}</p>
          {copy.sections.map((s, i) => (
            <section key={i} data-testid={`privacy-section-${i}`}>
              <div className="h-px bg-[#EEEEEE] mb-7" />
              <h2 className="font-serif-display text-[22px] text-[#1A2E35] mb-3">
                {s.h}
              </h2>
              {s.p && <p>{s.p}</p>}
              {s.list && (
                <ul className="list-disc pl-6 mt-3 space-y-1">
                  {s.list.map((item, j) => (
                    <li key={j}>{item}</li>
                  ))}
                </ul>
              )}
              {s.p_after && <p className="mt-3">{s.p_after}</p>}
              {s.contact && (
                <p className="mt-3">
                  <a
                    href={`mailto:${CONTACT}`}
                    className="text-[#0AADA0] hover:underline"
                    data-testid={`privacy-contact-link-${i}`}
                  >
                    {CONTACT}
                  </a>
                </p>
              )}
            </section>
          ))}
        </div>

        <div className="h-px bg-[#EEEEEE] my-10" />
        <p className="font-serif-display italic text-[#0AADA0] text-[20px] text-center">
          Aruba, Curaçao, Bonaire &amp; St. Maarten — caught up.
        </p>
      </article>
      <Footer />
    </div>
  );
}
